import { apiRequest } from '../lib/api-client';
import type { BadgeTone } from '../ui/Page';

/** Proposals, contracts, and the page a customer accepts on. */

export const PROPOSAL_STATUSES = [
  'DRAFT',
  'SENT',
  'VIEWED',
  'ACCEPTED',
  'DECLINED',
  'EXPIRED',
  'WITHDRAWN',
] as const;

export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number];

export interface ProposalItem {
  id: string;
  productId: string | null;
  description: string;
  quantity: number;
  unitPriceCents: number;
  discountBps: number;
  recurring: boolean;
  totalCents: number;
}

export interface Acceptance {
  name: string;
  email: string;
  ip: string | null;
  userAgent: string | null;
  acceptedAt: string;
  documentSha256: string;
}

export interface Proposal {
  id: string;
  opportunityId: string;
  companyId: string | null;
  contactId: string | null;
  number: number;
  version: number;
  title: string;
  introduction: string | null;
  terms: string | null;
  status: ProposalStatus;
  currency: string;
  totalCents: number;
  recurringCents: number;
  oneOffCents: number;
  termMonths: number;
  validityDays: number;
  sentAt: string | null;
  expiresAt: string | null;
  viewedAt: string | null;
  decidedAt: string | null;
  documentSha256: string | null;
  acceptance: Acceptance | null;
  declineReason: string | null;
  ownerId: string | null;
  createdAt: string;
}

export interface ProposalDetail extends Proposal {
  items: ProposalItem[];
  /** Present only in the response to `send`: the token is shown exactly once. */
  publicUrl?: string;
}

export function listProposals(
  query: { opportunityId?: string; status?: ProposalStatus[]; search?: string } = {},
): Promise<{ proposals: Proposal[] }> {
  const params = new URLSearchParams();

  if (query.opportunityId) params.set('opportunityId', query.opportunityId);
  if (query.search) params.set('search', query.search);
  for (const status of query.status ?? []) params.append('status', status);

  return apiRequest(`/v1/proposals?${params.toString()}`);
}

export function getProposal(id: string): Promise<ProposalDetail> {
  return apiRequest(`/v1/proposals/${id}`);
}

export function draftProposal(payload: {
  opportunityId: string;
  title?: string;
  termMonths?: number;
  validityDays?: number;
}): Promise<ProposalDetail> {
  return apiRequest('/v1/proposals', { method: 'POST', body: payload });
}

export function editProposal(
  id: string,
  payload: {
    title?: string;
    introduction?: string | null;
    terms?: string | null;
    termMonths?: number;
    validityDays?: number;
  },
): Promise<ProposalDetail> {
  return apiRequest(`/v1/proposals/${id}`, { method: 'PATCH', body: payload });
}

export function sendProposal(id: string): Promise<ProposalDetail> {
  return apiRequest(`/v1/proposals/${id}/send`, { method: 'POST' });
}

export function withdrawProposal(id: string): Promise<Proposal> {
  return apiRequest(`/v1/proposals/${id}/withdraw`, { method: 'POST' });
}

export function proposalDocument(id: string): Promise<{ url: string; sha256: string }> {
  return apiRequest(`/v1/proposals/${id}/document`);
}

export interface Contract {
  id: string;
  proposalId: string;
  opportunityId: string | null;
  companyId: string | null;
  number: number;
  status: 'ACTIVE' | 'ENDED' | 'CANCELED';
  currency: string;
  totalCents: number;
  monthlyCents: number;
  termMonths: number;
  startsOn: string;
  endsOn: string | null;
  renewalDate: string | null;
  signedAt: string;
  esignProvider: string | null;
  esignReference: string | null;
  endedAt: string | null;
  endReason: string | null;
}

export function listContracts(
  query: { companyId?: string } = {},
): Promise<{ contracts: Contract[] }> {
  const params = new URLSearchParams();
  if (query.companyId) params.set('companyId', query.companyId);

  return apiRequest(`/v1/contracts?${params.toString()}`);
}

export function endContract(
  id: string,
  payload: { status: 'ENDED' | 'CANCELED'; reason: string },
): Promise<Contract> {
  return apiRequest(`/v1/contracts/${id}/end`, { method: 'POST', body: payload });
}

/** The customer's side. No session: the token is the whole credential. */
export interface PublicProposal {
  number: number;
  version: number;
  title: string;
  introduction: string | null;
  terms: string | null;
  status: ProposalStatus;
  currency: string;
  totalCents: number;
  recurringCents: number;
  oneOffCents: number;
  termMonths: number;
  expiresAt: string | null;
  from: string;
  to: string | null;
  items: {
    description: string;
    quantity: number;
    unitPriceCents: number;
    discountBps: number;
    recurring: boolean;
    totalCents: number;
  }[];
  documentUrl: string | null;
  open: boolean;
}

export function viewPublicProposal(token: string): Promise<PublicProposal> {
  return apiRequest(`/pub/proposals/${token}`);
}

export function acceptPublicProposal(
  token: string,
  payload: { name: string; email: string },
): Promise<{ status: ProposalStatus; contractNumber: number }> {
  return apiRequest(`/pub/proposals/${token}/accept`, { method: 'POST', body: payload });
}

export function declinePublicProposal(
  token: string,
  reason: string,
): Promise<{ status: ProposalStatus }> {
  return apiRequest(`/pub/proposals/${token}/decline`, { method: 'POST', body: { reason } });
}

export function proposalTone(status: ProposalStatus): BadgeTone {
  if (status === 'ACCEPTED') return 'positive';
  if (status === 'DECLINED' || status === 'EXPIRED') return 'danger';
  if (status === 'WITHDRAWN' || status === 'DRAFT') return 'neutral';
  if (status === 'VIEWED') return 'warning';

  return 'info';
}
